import './ag-grid-styles.scss';

import type {
	CellFocusedEvent,
	ColDef,
	ColumnState,
	GridApi,
	GridReadyEvent,
	RowDataUpdatedEvent,
	SortChangedEvent,
} from 'ag-grid-community';
import dayjs, { type Dayjs } from 'dayjs';
import {
	addDays,
	dataDate,
	dateFormatDisplay,
	today,
} from 'features/dates/date-helpers';
import {
	selectRecReviewTab,
	selectShowSearchPanel,
	selectTableSort,
	selectTabs,
} from 'features/my-view/redux/selectors';
import { useEffect, useState } from 'react';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { DisplayedColumnsChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import styles from 'common/_variables.module.scss';
import { usePropertyContext } from 'context/propertyContext';
import { selectShowAskRev } from 'features/header/redux/selectors';
import { setTableSort } from 'features/my-view/redux/my-view-slice';
import { useRateUpload } from 'features/rate-upload/hooks/use-rate-upload';
import { useUser } from 'features/users/context/userContext';
import { BiArrowToTop } from 'react-icons/bi';
import { BsCalendar2Check } from 'react-icons/bs';
import { RecReviewStatus } from 'types/RecReviewTypes';
import { CustomHeader } from './DashboardCustomHeader';
import { NumericCellEditor } from './NumericCellEditor';
import type { RecReviewData } from './useDashboardController';

const defaultColOptions: ColDef<RecReviewData> = {
	sortable: true,
	lockVisible: true,
	suppressSizeToFit: true,
};

const components: Record<string, any> = {
	agColumnHeader: CustomHeader,
	numericCellEditor: NumericCellEditor,
};

export const DashboardTable = (props: {
	data: any;
	columns: any;
	gridRef: React.RefObject<AgGridReact>;
	paginationMaxDuration: number;
	fixedColsLength: number;
	handleColumnMoved: (event: any) => void;
	showMessage?: string;
	// NEW: AI sorting parameters
	aiSorting?: {
		sortBy: string | null;
		sortDirection: 'asc' | 'desc' | null;
		sortLimit: number | null;
	} | null;
}) => {
	const showSearchPanel = useAppSelector(selectShowSearchPanel);
	const showAskRev = useAppSelector(selectShowAskRev);
	const {
		data,
		columns,
		gridRef,
		fixedColsLength,
		handleColumnMoved,
		showMessage,
		aiSorting,
	} = props;
	const colSort = useAppSelector(selectTableSort);
	const tabColumns = useAppSelector(selectTabs);
	const currentTab = useAppSelector(selectRecReviewTab);
	const dispatch = useAppDispatch();
	const { handleRowSelection, WarningModal } = useRateUpload();
	const [isForcePickerOpen, setIsOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<any | null>(null);
	const [selectedDate, handleDateChange] = useState(today);
	const { isReadOnly } = useUser();
	const { showPast } = usePropertyContext();

	const orderedColumns = (): ColumnState[] => {
		const currentTabColumns = tabColumns
			? tabColumns[currentTab as keyof typeof tabColumns]
			: [];

		return currentTabColumns?.length
			? [
					...columns.slice(0, fixedColsLength).map((col: any) => {
						return {
							colId: col.field?.toString() || `column-${Math.random()}`,
						};
					}),
					...currentTabColumns.map((col) => {
						return { colId: col?.toString() || `column-${Math.random()}` };
					}),
				]
			: columns.map((col: any) => {
					return { colId: col.field?.toString() || `column-${Math.random()}` };
				});
	};

	useEffect(() => {
		if (colSort) {
			const columns = gridRef?.current?.columnApi?.getColumnState();
			const column = columns?.find((col) => col.colId === colSort.column);
			if (column) {
				const updatedCol = {
					...column,
					sort: colSort.direction,
				};
				const updatedState = columns?.map((col) => {
					if (col.colId === colSort.column) {
						return updatedCol;
					}
					return {
						...col,
						sort: null,
					};
				}) as ColumnState[];

				if (updatedState) {
					gridRef?.current?.columnApi.applyColumnState({
						state: updatedState,
					});
				}
			}
		}
	}, [colSort, gridRef, columns]);

	// Apply AI-suggested sorting through AG-Grid's API
	useEffect(() => {
		if (
			aiSorting?.sortBy &&
			aiSorting?.sortDirection &&
			gridRef?.current?.columnApi &&
			gridRef?.current?.api
		) {
			const allColumns = gridRef.current.columnApi.getColumnState() || [];
			const targetColumn = allColumns.find(
				(col) => col.colId === aiSorting.sortBy,
			);

			if (!targetColumn) {
				console.warn(
					`[AI Sorting] Column "${aiSorting.sortBy}" not found in current tab "${currentTab}". Available columns:`,
					allColumns.map((c) => c.colId),
				);
				return;
			}

			console.log(
				`[AI Sorting] Applying sort: ${aiSorting.sortBy} ${aiSorting.sortDirection} (tab: ${currentTab})`,
			);

			const newColumnState = allColumns.map((col) => {
				if (col.colId === aiSorting.sortBy) {
					return {
						...col,
						sort: aiSorting.sortDirection,
						sortIndex: 0,
					};
				}
				return {
					...col,
					sort: null,
					sortIndex: undefined,
				};
			});

			gridRef.current.columnApi.applyColumnState({
				state: newColumnState,
				applyOrder: true,
			});
		} else if (!aiSorting?.sortBy && gridRef?.current?.columnApi) {
			// When AI sort is cleared, reset to default date sort
			const allCols = gridRef.current.columnApi.getColumnState() || [];
			const withSort = allCols.map((col) => ({
				...col,
				sort: col.colId === 'stay_date' ? ('asc' as const) : null,
			}));
			gridRef.current.columnApi.applyColumnState({
				state: withSort,
				applyOrder: true,
			});
		}
	}, [aiSorting, gridRef, currentTab]);

	const gridApi: GridApi | undefined = gridRef?.current?.api;

	useEffect(() => {
		if (!gridApi) return;

		// Get the row count to ensure we're not using an invalid index
		const rowCount = gridApi.getDisplayedRowCount();
		if (rowCount === 0) return;

		if (showPast) {
			// Use a safer index that's within bounds
			const safeIndex = Math.min(7, rowCount - 1);
			gridApi.ensureIndexVisible(safeIndex, 'top');
		} else {
			gridApi.ensureIndexVisible(0, 'top');
		}
	}, [showPast, gridApi]);

	const handleCellFocused = (event: CellFocusedEvent) => {
		const { rowIndex: prevRow, column } = event;
		const nextRow = event.api.getFocusedCell()?.rowIndex;

		if (!column || !nextRow || prevRow === nextRow) return;
		event.api.startEditingCell({
			rowIndex: nextRow,
			colKey: column,
		});
	};

	const handleGridReady = (evt: GridReadyEvent) => {
		const columns = orderedColumns();
		evt.api.sizeColumnsToFit();
		evt.columnApi.applyColumnState({ state: columns, applyOrder: true });
		// Set default sort to stay_date ascending
		setTimeout(() => {
			const allCols = evt.columnApi.getColumnState() || [];
			const withSort = allCols.map((col) => ({
				...col,
				sort: col.colId === 'stay_date' ? ('asc' as const) : null,
			}));
			evt.columnApi.applyColumnState({ state: withSort, applyOrder: true });
		}, 0);
	};

	const handleRowDataUpdated = (evt: RowDataUpdatedEvent) => {
		evt.api.forEachNode((row) => {
			const isSelected = row.isSelected();
			if (
				row.data &&
				row.data.rec_status === RecReviewStatus.PENDING &&
				!isSelected
			) {
				row.setSelected(true, false, true);
			}
		});
	};

	const handleFirstDataRendered = () => {
		if (!gridApi || !showPast) return;

		const rowCount = gridApi.getDisplayedRowCount();
		if (rowCount === 0) return;

		// Use a safer index that's within bounds
		const safeIndex = Math.min(7, rowCount - 1);
		gridApi.ensureIndexVisible(safeIndex, 'top');
	};

	const jumpToRowDateSelection = (date: string) => {
		if (!gridApi || !data?.length) return;

		const formatDate = dataDate(date);
		const index = data.findIndex(
			(x: any) => dataDate(x.stay_date) === formatDate,
		);

		// Only scroll if a valid index was found
		if (index >= 0 && index < gridApi.getDisplayedRowCount()) {
			gridApi.ensureIndexVisible(index, 'top');
		}
	};

	const handleSortChanged = (evt: SortChangedEvent) => {
		const colState = evt.columnApi.getColumnState();
		const sortedCol = colState.find((col) => col.sort);
		if (sortedCol) {
			const payload = {
				column: sortedCol.colId,
				direction: sortedCol.sort || null,
			};
			dispatch(setTableSort(payload));
		}
	};

	const handleTabChange = (evt: DisplayedColumnsChangedEvent) => {
		const columns = orderedColumns();
		// Ensure all colIds are strings before applying column state
		const safeColumns = columns.map((col) => ({
			...col,
			colId: col.colId?.toString() || '',
		}));
		evt.columnApi.applyColumnState({ state: safeColumns, applyOrder: true });
	};

	const backToTop = () => {
		if (!gridApi) return;

		const rowCount = gridApi.getDisplayedRowCount();
		if (rowCount === 0) return;

		// Use a safer index that's within bounds
		const safeIndex = Math.min(7, rowCount - 1);
		gridApi.ensureIndexVisible(safeIndex);
	};

	const jumpForward = () => {
		if (!gridApi) return;

		const lastRow = gridApi.getLastDisplayedRow();
		if (lastRow !== null && lastRow !== undefined && lastRow >= 0) {
			gridApi.ensureIndexVisible(lastRow, 'top');
		}
	};

	const jumpBack = () => {
		if (!gridApi) return;

		const firstRow = gridApi.getFirstDisplayedRow();
		if (firstRow !== null && firstRow !== undefined && firstRow >= 0) {
			gridApi.ensureIndexVisible(firstRow, 'bottom');
		}
	};

	const handleIsRowSelectable = (rowNode: any) => {
		return dayjs(rowNode.data.stay_date).isSameOrAfter(dayjs(), 'day');
	};

	return (
		<>
			<div className='dashboard__tables'>
				<div
					className={`ag-theme-alpine ag-grid-container tw-h-[calc(100vh-275px)] tw-min-h-[39rem] tw-w-full ${
						showAskRev ? 'askForm-vis' : ''
					} ${showSearchPanel ? 'sForm-vis' : ''}`}
				>
					{data && (
						<>
							<AgGridReact
								columnDefs={columns}
								onColumnMoved={handleColumnMoved}
								components={components}
								rowData={data}
								defaultColDef={defaultColOptions}
								tooltipShowDelay={800}
								enterMovesDown={true}
								enterMovesDownAfterEdit={true}
								onCellFocused={handleCellFocused}
								onNewColumnsLoaded={handleTabChange}
								getRowId={(params) => params.data.stay_date}
								onGridReady={handleGridReady}
								onRowSelected={handleRowSelection}
								onSortChanged={handleSortChanged}
								onRowDataUpdated={handleRowDataUpdated}
								ref={gridRef}
								rowBuffer={0}
								rowMultiSelectWithClick={true}
								rowSelection='multiple'
								suppressContextMenu={true}
								suppressRowClickSelection={isReadOnly}
								rowModelType='clientSide'
								cacheBlockSize={365}
								cacheOverflowSize={10}
								maxConcurrentDatasourceRequests={2}
								infiniteInitialRowCount={365}
								maxBlocksInCache={2}
								rowHeight={46}
								headerHeight={75}
								maintainColumnOrder={true}
								stopEditingWhenCellsLoseFocus={true}
								onFirstDataRendered={handleFirstDataRendered}
								isRowSelectable={handleIsRowSelectable}
								icons={{
									columnMoveHide: '<br>',
								}}
								overlayNoRowsTemplate={`<div style="padding: 10px; display: block; text-align: center;">${showMessage}</div>`}
							/>
							<WarningModal />
						</>
					)}
					<div className='dashboard__pagination-container'>
						<div className='dashboard__pagination'>
							<div className='dashboard__pagination-wrapper'>
								<IconButton
									className='dashboard__pagination-row'
									disableFocusRipple
									disableRipple
									aria-label='prev-rows'
									onClick={jumpBack}
								>
									<MdKeyboardArrowUp size={20} color={styles.colorPrimary} />
								</IconButton>
								<div className='dashboard__pagination-row'>
									<DatePicker
										open={isForcePickerOpen}
										onClose={() => setIsOpen(false)}
										value={selectedDate}
										onChange={(newDate: Dayjs | null) => {
											handleDateChange(newDate!.format(dateFormatDisplay));
											jumpToRowDateSelection(
												newDate!.format(dateFormatDisplay),
											);
										}}
										inputFormat={'YYYY-MM-DD'}
										maxDate={addDays(props.paginationMaxDuration - 1)}
										disablePast
										PopperProps={{
											placement: 'bottom-end',
											anchorEl: anchorEl,
										}}
										renderInput={(_params) => (
											<Button
												className='dashboard__pagination-calender'
												variant='contained'
												onClick={(e) => {
													setIsOpen((isOpen) => !isOpen);
													setAnchorEl(e.currentTarget);
												}}
											>
												<BsCalendar2Check
													size={20}
													color={styles.colorPrimary}
												/>
											</Button>
										)}
									/>
								</div>
								<IconButton
									className='dashboard__pagination-row'
									disableFocusRipple
									disableRipple
									aria-label='next-rows'
									onClick={jumpForward}
								>
									<MdKeyboardArrowDown size={20} color={styles.colorPrimary} />
								</IconButton>
							</div>
						</div>

						<IconButton
							className='dashboard__pagination-row'
							disableFocusRipple
							disableRipple
							aria-label='first-page'
							onClick={backToTop}
						>
							<div className='dashboard__back-to-top'>
								<BiArrowToTop size={20} color={styles.whiteColor} />
							</div>
						</IconButton>
					</div>
				</div>
			</div>
		</>
	);
};
